import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { shopTalk } from '../services/recordShop';
import { applyRoleOut } from '../services/worldJob';
import { useCounterItems } from '../state/counterItems';
import { useGymFuel, drinkFuel } from '../state/gymFuel';
import { usePrintStudio } from '../state/printStudio';
import { lineClip } from '../services/ensemble';
import { audioEngine } from '../services/audioEngine';
import { edgeInset, IS_GUEST, guestEmail } from '../config';
import useAvatarVoiceChat from '../hooks/useAvatarVoiceChat';
import { useStackSlot, DOCK_MAX_H } from './hudStack';
import { useSceneChat, useNarrow, sceneWrap, SceneHead, useEscToLeave, useChatSpeaking } from './SceneChat';

// ShopDock — the counter at Wax & Vinyl.
//
// Khari, 2026-08-04: "he will have tool calling to get info about (and
// recommend) any track in our database, determined by what the user is looking
// for.. As he makes suggestions, they will show on the wall where the records
// are at."
//
// THE WALL IS THE OUTPUT, NOT THIS PANEL. Everything he pulls goes on the
// sleeves behind him — this is only the talking. So the dock stays deliberately
// small and does not list tracks: two surfaces showing the same six records
// would make the wall decorative, and the wall is the thing you can play, like
// and (later) buy from. When he changes it, this says one quiet line and gets
// out of the way.
//
// HE SPEAKS. The reply carries his voice on the transient path (flash model,
// memory only — a clerk's sentences are one-offs and never worth caching to a
// bucket), as a URL when it is ready or a poll key while it renders. Text lands
// first either way; a line that never arrives just stays written.
//
// The turn contract (message + history in, reply + shelf + clip out) is the
// durable part; when he moves to the ConvAI agent the mic opens and the wall
// gets painted by a CLIENT tool instead of by this reply, and nothing else here
// has to change.
//
// ⚠ THE MIC OPENED (2026-10-30). Khari: *"Voice chat doesn't support jobs yet,
// so ordering is text only. — Please add it. If this is a new tool call, we
// should make it generic enough to handle more items like this. The music
// genius and the barista are the first two examples to use it."* 🎙 starts the
// SAME voice call the rest of the World uses (useAvatarVoiceChat), started IN
// THE JOB: it names this room and this job, the server answers with whoever is
// on shift and the job's tools, and every tool they reach for comes back as the
// same bag the typed reply carries — applied by the one function both use
// (services/worldJob applyRoleOut). Text stays: typing during a call goes into
// the call, typing without one is the counter's ordinary turn.
const ACCENT = '#9dff7d';

const wrap = {
  // Stands on the player bar, and publishes its own height so Done stands on
  // it (hudStack) — it sat over the radio, with Done on its Send (2026-10-31).
  position: 'fixed', right: edgeInset(16, 'right'), bottom: 'calc(max(16px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px))',
  zIndex: 70, width: 'min(340px, calc(100vw - 32px))', maxHeight: DOCK_MAX_H(460),
  display: 'flex', flexDirection: 'column', pointerEvents: 'auto', fontFamily: 'system-ui, sans-serif',
  background: 'rgba(8,12,9,0.94)', border: '1px solid rgba(157,255,125,0.34)', borderRadius: 16,
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
const head = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '11px 13px', borderBottom: '1px solid rgba(157,255,125,0.18)',
};
const bubble = (mine) => ({
  alignSelf: mine ? 'flex-end' : 'flex-start',
  maxWidth: '86%', padding: '8px 11px', borderRadius: 12,
  background: mine ? 'rgba(157,255,125,0.14)' : 'rgba(255,255,255,0.07)',
  color: mine ? '#e8ffe0' : '#eef2ea', font: '14px/1.4 system-ui',
});
const chip = {
  cursor: 'pointer', background: 'rgba(157,255,125,0.1)', color: ACCENT,
  border: '1px solid rgba(157,255,125,0.32)', borderRadius: 999,
  padding: '6px 11px', font: '12.5px system-ui', whiteSpace: 'nowrap',
};
const inputStyle = {
  flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(157,255,125,0.28)',
  background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 15, fontFamily: 'inherit',
  outline: 'none', boxSizing: 'border-box', minWidth: 0,
};

// What people actually walk in and say. Three, because a wall of suggestions is
// a form, and a form is the thing this whole feature exists to not be.
const OPENERS = ['Something uptempo', 'New R&B', 'Something moody for tonight'];

// THE SAME COUNTER AT A CAFÉ (The Jahva House, 2026-10-30): a room with tables
// is a café, and the one behind its bar is the barista (API CafeService). Same
// turn, same voice; warmer words and colours, where you are sent with every
// line (your table, or how many are free), and the drink they make followed
// until it is drunk (CafeCounter).
const CAFE = {
  accent: '#ffc27a', line: 'rgba(255,194,122,', bg: 'rgba(16,10,6,0.94)', ink: '#1a0f06', userInk: '#fff3e2',
  role: 'barista', hello: 'What can I get you?', openers: ['A flat white', 'Something strong', 'What would you pour me?'],
  busy: 'reaching for a cup…', placeholder: 'Order something, or just talk…', send: 'Say',
};
const COFFEE = {
  making: (d) => `☕ Making your ${d}`,
  serving: (d) => `☕ Your ${d} is on its way to your table`,
  table: (d) => `☕ Your ${d} is on your table`,
  bar: (d) => `☕ Your ${d} is waiting at the end of the bar`,
};

// THE SAME COUNTER AT THE CORNER MARKET (2026-10-30). Khari: "another type of
// job: grocer/cashier. We have a corner market with a job for an artist to work
// the cash register." A room with a `counter` spot (and no tables) is a market,
// and the one behind it is the cashier (API GroceryService ring_up). Same turn,
// same voice; the shop's fluorescent yellow, and what they rang up followed from
// the scanner to the bag on the counter (state/counterItems → CounterItems).
const MARKET = {
  accent: '#ffd27d', line: 'rgba(255,210,125,', bg: 'rgba(14,12,6,0.94)', ink: '#1a1406', userInk: '#fff6dd',
  role: 'cashier', hello: 'Find everything okay?', openers: ['Just these', 'Got any snacks?', 'What\'s good tonight?'],
  busy: 'scanning…', placeholder: 'Tell them what you\'ve got, or just talk…', send: 'Say',
};
const BAG = {
  ringing: (n) => `🛍 Ringing up ${n === 1 ? 'your item' : `${n} items`}…`,
  bagged: (items) => `🛍 Bagged and on the counter: ${items.join(', ')}`,
};

// THE SAME COUNTER AT THE GANGSTAFUEL GYM (2026-10-31). Khari: "This will add
// the position of 'fitness instructor'. They do not give fitness advice, they
// just encourage you to workout. Like the coffee shop, a drink is available."
// A room with a fuel bar (rooms.js `fuel`) is the gym, and the one there is the
// fitness instructor (API GymService hand_fuel). Black and lime; the can they
// hand over followed onto the bar, and drunk from here or by tapping it there
// (state/gymFuel → GymFloor, EnergyBoost).
const GYM = {
  accent: '#74f84b', line: 'rgba(116,248,75,', bg: 'rgba(8,10,8,0.95)', ink: '#07140a', userInk: '#eaffdf',
  role: 'fitness instructor', hello: 'You showed up. That\'s the hard part.', openers: ['Hype me up', 'Got a GangstaFuel?', 'What should I hit first?'],
  busy: 'between reps…', placeholder: 'Talk, or ask for a GangstaFuel…', send: 'Say',
};

// THE SAME COUNTER AT THE HYPEFLYER PRINT SHOP (2026-10-31). Khari: "a front
// desk with a clerk and next to them can be the options on a panel/modal as
// they walk you through them." A `printshop` room's counter opens THE STUDIO
// beside it (components/printshop/PrintStudio), and the clerk turns it to what
// is being made (API PrintShopService show_studio → applyRoleOut `studio`).
// What the studio makes comes back here as the quiet note line.
const PRINT = {
  accent: '#ff4fa3', line: 'rgba(255,79,163,', bg: 'rgba(14,9,18,0.95)', ink: '#170910', userInk: '#ffe9f4',
  role: 'clerk', hello: 'What are we making today?', openers: ['A new headshot', 'A post for my artist', 'A Who Is card'],
  busy: 'pulling it up…', placeholder: 'Tell them what you\'re making, or just talk…', send: 'Say',
};

export default function ShopDock() {
  const shop = useWorldStore((s) => s.shop);
  const closeShop = useWorldStore((s) => s.closeShop);
  const cafe = !!(shop?.zone && ROOMS[shop.zone]?.tables?.length);
  const cup = useWorldStore((s) => (cafe && s.cafeOrder?.zone === shop?.zone ? s.cafeOrder : null));
  const gym = !cafe && !!(shop?.zone && ROOMS[shop.zone]?.fuel);
  const can = useGymFuel((s) => (gym && s.can?.zone === shop?.zone ? s.can : null));
  const print = !cafe && !gym && !!(shop?.zone && ROOMS[shop.zone]?.printshop);
  const market = !cafe && !gym && !print && !!(shop?.zone && ROOMS[shop.zone]?.counter);
  const bag = useCounterItems((s) => (market && s.current?.zone === shop?.zone ? s.current : null));
  // The look and the words: the café's, the market's, or (null) the record shop's own.
  const skin = cafe ? CAFE : gym ? GYM : print ? PRINT : market ? MARKET : null;

  const slot = useStackSlot();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const endRef = useRef(null);

  // ── TALKING OUT LOUD, IN THE JOB ──────────────────────────────────────────
  //
  // No avatar id: the call names the room and the job, and the server answers
  // with whoever is on shift (see the header). What they say and what you say
  // land in the same thread of bubbles as typing, so the conversation reads as
  // one, and a typed turn after the call carries it as history. A guest talks
  // on the email-capped guest call once they have given an email elsewhere in
  // the World; without one the counter is text, as it always was.
  const talk = useAvatarVoiceChat({
    avatarId: null,
    guest: IS_GUEST,
    email: IS_GUEST ? guestEmail() : '',
    job: shop?.actorKey ? {
      zone: shop.zone, roleKey: shop.actorKey,
      seat: () => (cafe ? useWorldStore.getState().cafeSeat : null),
      onNote: (n) => setNote(n),
    } : null,
    onReply: (text) => setMessages((cur) => [...cur, { role: 'assistant', text }]),
    onHeard: (text) => setMessages((cur) => [...cur, { role: 'user', text }]),
  });
  const canTalk = !IS_GUEST || !!guestEmail();
  // Closing the counter or walking to another hangs up — read through refs so
  // the cleanup always ends the live call, not the one from an older render.
  // Only a call that is open: end() also un-ducks the music and clears the
  // World's talking flag, which another dock may own.
  const endTalk = useRef(talk.end);
  endTalk.current = talk.end;
  const talkLive = useRef(false);
  talkLive.current = talk.active || talk.phase === 'connecting' || talk.requestingMic;
  useEffect(() => () => { if (talkLive.current) endTalk.current(); }, [shop?.actorKey, shop?.zone]);

  // A new counter is a new conversation. Nothing carries over from the last
  // clerk you talked to, which matters the moment there are two of them.
  useEffect(() => {
    if (!shop) return;
    setMessages([]); setInput(''); setNote('');
  }, [shop?.actorKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  // At the print shop the studio opens beside the counter (its four choices,
  // or wherever it was), and what it makes comes back as the note line.
  const showStudio = usePrintStudio((st) => st.show);
  useEffect(() => { if (print && shop?.zone) showStudio({ zone: shop.zone }); }, [print, shop?.zone, showStudio]);
  useEffect(() => {
    if (!print) return undefined;
    const onNote = (e) => { if (e?.detail) setNote(String(e.detail)); };
    window.addEventListener('ht:shop-note', onNote);
    return () => window.removeEventListener('ht:shop-note', onNote);
  }, [print]);

  // WALKING OUT ENDS THE CONVERSATION. The counter belongs to a room, and a dock
  // that survived the doorway would leave you talking to a clerk you can no
  // longer see — and would leave his records hanging on a wall in another shop.
  const interior = useWorldStore((s) => s.interior);
  useEffect(() => {
    if (shop?.zone && interior !== shop.zone) closeShop();
  }, [interior, shop?.zone, closeShop]);

  // ── HIS VOICE ─────────────────────────────────────────────────────────────
  //
  // Khari, 2026-08-04: "the music genius should speak, not just text."
  //
  // One audio element for the counter, so a new answer cuts off the last one
  // rather than talking over it — the same single-source rule the rest of the
  // world's speech follows. The music DIPS to the standard duck while he talks
  // (Khari, 2026-08-03: "not muted.. same level of standard ducking") and comes
  // back up when he stops, including when you walk out mid-sentence.
  const voice = useRef(null);
  const ducked = useRef(false);
  const [voicePlaying, setVoicePlaying] = useState(false);   // their face moves while it plays

  const undip = () => { if (ducked.current) { ducked.current = false; audioEngine.duck(false); } };
  const play = (url) => {
    try {
      voice.current?.pause();
      const a = new Audio(url);
      voice.current = a;
      a.onplaying = () => setVoicePlaying(true);
      a.onpause = () => setVoicePlaying(false);
      a.onended = () => { undip(); setVoicePlaying(false); };
      a.onerror = () => { undip(); setVoicePlaying(false); };
      if (!ducked.current) { ducked.current = true; audioEngine.duck(true); }
      a.play().catch(() => undip());   // autoplay refused — the text still reads
    } catch { undip(); }
  };

  // A clip that was still rendering when the reply landed. Same poll route every
  // other spoken line uses; it gives up rather than nagging, and a line that
  // never arrives simply stays text.
  const speak = (d) => {
    if (d.voiced === false) {
      // Loud in the console, quiet in the room: the customer does not need to
      // know the casting is unfinished, and whoever is building it does.
      console.warn(`[shop] ${shop?.name || 'clerk'} is silent — ${d.voiceReason || 'no voice'}`);
      setNote('no voice cast for this part yet');
      return;
    }
    if (d.audioUrl) { play(d.audioUrl); return; }
    if (!d.audioKey) return;
    // TIGHT POLL, because this gap is the whole perceived latency of his voice:
    // the flash synth takes ~1-2s, so asking at 600ms + 700ms intervals could
    // sit on a finished clip for most of a second. First ask at 350ms, then
    // every 400ms — same ~9s budget, a third of the wait.
    let tries = 0;
    const tick = async () => {
      tries += 1;
      const url = await lineClip(d.audioKey);
      if (url) { play(url); return; }
      if (tries >= 22) { console.warn('[shop] clip never landed — the line stays text'); return; }
      setTimeout(tick, 400);
    };
    setTimeout(tick, 350);
  };

  useEffect(() => () => { voice.current?.pause(); undip(); }, []);   // leaving stops him

  // A COUNTER AS A SCENE (rooms.js `chatScene`: the gym, components/SceneChat):
  // laid over the shot once the camera has them; Esc leaves. Their face moves
  // while their voice plays or a reply is read out (StagedResident).
  const sceneOn = useSceneChat((st) => st.on) && !!shop?.scene;
  const narrow = useNarrow();
  useEscToLeave(sceneOn, closeShop);
  const lastReply = [...messages].reverse().find((m) => m.role === 'assistant')?.text || null;
  useChatSpeaking(shop?.actorKey ? `actor:${shop.actorKey}` : null, { voice: talk.phase === 'speaking' || voicePlaying, reply: lastReply });

  if (!shop) return null;

  const send = async (text) => {
    const t = String(text || '').trim();
    if (!t || busy) return;
    // ON A CALL, a typed line goes into the call (the same agent answers it out
    // loud) instead of starting a second conversation by text.
    if (talk.active && talk.say(t)) {
      setMessages((cur) => [...cur, { role: 'user', text: t }]);
      setInput('');
      setNote('');
      return;
    }
    const history = messages.map((m) => ({ role: m.role, content: m.text }));
    setMessages((cur) => [...cur, { role: 'user', text: t }]);
    setInput('');
    setBusy(true);
    setNote('');
    try {
      const d = await shopTalk({ actorKey: shop.actorKey, zone: shop.zone, message: t, history, seat: cafe ? useWorldStore.getState().cafeSeat : null });
      setMessages((cur) => [...cur, { role: 'assistant', text: d.reply || '…' }]);
      speak(d);
      // WHAT THEY DID, applied the one way voice applies it too (worldJob): a
      // drink they MADE (take_order, café rooms only — never one guessed from
      // their words); a bag they RANG UP (ring_up, rooms with a counter); the
      // wall only when he actually changed it (re-hanging an identical shelf
      // would restart every animated sleeve); a saved like.
      const { note: done } = applyRoleOut(d, { zone: shop.zone, roleKey: shop.actorKey });
      if (done != null) setNote(done);
    } catch (err) {
      setMessages((cur) => [...cur, { role: 'assistant', text: err.message || 'The counter is busy — try again.' }]);
    } finally {
      setBusy(false);
    }
  };

  const accent = skin ? skin.accent : ACCENT;
  const line = (a) => (skin ? `${skin.line}${a})` : `rgba(157,255,125,${a})`);
  const coffee = cup ? (cup.phase === 'making' ? COFFEE.making(cup.drink) : cup.phase === 'serving' ? COFFEE.serving(cup.drink)
    : cup.table ? COFFEE.table(cup.drink) : COFFEE.bar(cup.drink)) : '';
  // What the cashier rang up, from the scanner to the counter (gone with the bag).
  const bagLine = bag ? (bag.phase === 'ringing' ? BAG.ringing(bag.items.length) : BAG.bagged(bag.items)) : '';

  // The call, in one quiet line: what it is doing, or why it did not start
  // (a text-only counter, the free voice time used up, the mic blocked). The
  // mic stays pressable after an error — pressing again is the retry.
  const onCall = talk.active || talk.phase === 'connecting' || talk.requestingMic;
  const callLine = talk.requestingMic ? 'allow the mic to talk…'
    : talk.phase === 'connecting' ? 'calling the counter…'
      : talk.active ? (talk.phase === 'speaking' ? '● speaking — talk over them any time' : '● listening — talk, or type')
        : talk.error || '';
  const toggleCall = () => {
    // Their typed reply's clip stops: one voice at the counter at a time.
    if (!onCall) { try { voice.current?.pause(); } catch { /* noop */ } undip(); }
    talk.toggleTalk();   // inside the click — the mic and audio unlock need the gesture
  };

  return (
    <div ref={sceneOn ? undefined : slot} style={sceneOn ? sceneWrap(narrow) : skin ? { ...wrap, background: skin.bg, border: `1px solid ${line(0.34)}` } : wrap}>
      {sceneOn ? <SceneHead name={shop.name || 'The counter'} sub={shop.role || skin?.role || 'record shop'} onClose={closeShop} narrow={narrow} accent={accent} /> : (
      <div style={skin ? { ...head, borderBottom: `1px solid ${line(0.18)}` } : head}>
        <div>
          <div style={{ font: '600 14px system-ui', color: '#eef7ea' }}>{shop.name || 'The counter'}</div>
          <div style={{ font: '10.5px system-ui', letterSpacing: 1.1, textTransform: 'uppercase', color: accent, opacity: 0.8 }}>
            {shop.role || skin?.role || 'record shop'}
          </div>
        </div>
        <button onClick={closeShop} style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#cfe8c6', fontSize: 20, lineHeight: 1, padding: 4 }} aria-label="Leave the counter">×</button>
      </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 13px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!messages.length && (
          <>
            <div style={{ ...bubble(false) }}>
              {skin ? skin.hello : <>What are you after? Give me a genre, a tempo, or just a feeling — I&apos;ll pull something.</>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
              {(skin ? skin.openers : OPENERS).map((o) => (
                <button key={o} style={skin ? { ...chip, background: line(0.1), color: accent, border: `1px solid ${line(0.32)}` } : chip} onClick={() => send(o)}>{o}</button>
              ))}
            </div>
          </>
        )}
        {messages.map((m, i) => (
          <div key={i} style={skin && m.role === 'user' ? { ...bubble(true), background: line(0.14), color: skin.userInk } : bubble(m.role === 'user')}>{m.text}</div>
        ))}
        {busy && <div style={{ ...bubble(false), opacity: 0.6 }}>{skin ? skin.busy : 'flipping through the racks…'}</div>}
        {note && <div style={{ alignSelf: 'flex-start', font: '11.5px system-ui', color: accent, opacity: 0.85 }}>{note}</div>}
        {coffee && <div style={{ alignSelf: 'flex-start', font: '11.5px system-ui', color: accent, opacity: 0.9 }}>{coffee}</div>}
        {bagLine && <div style={{ alignSelf: 'flex-start', font: '11.5px system-ui', color: accent, opacity: 0.9 }}>{bagLine}</div>}
        {can && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8, font: '11.5px system-ui', color: accent }}>
            ⚡ Your {can.drink} is on the bar
            <button style={{ ...chip, background: line(0.16), color: accent, border: `1px solid ${line(0.45)}`, padding: '4px 10px', fontWeight: 700 }} onClick={() => drinkFuel()}>Drink it</button>
          </div>
        )}
        {callLine &&<div style={{ alignSelf: 'flex-start', font: '11.5px system-ui', color: talk.error && !onCall ? '#ff9a9a' : accent, opacity: 0.85 }}>{callLine}</div>}
        <div ref={endRef} />
      </div>

      {IS_GUEST && !skin && (
        <div style={{ padding: '0 13px 6px', font: '11px system-ui', color: 'rgba(255,255,255,0.5)' }}>
          Browsing as a guest — sign in to save records to your likes.
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        style={{ display: 'flex', gap: 7, padding: '10px 12px 12px', borderTop: `1px solid ${line(0.16)}` }}
      >
        {/* Never disabled while they think: the SEND waits, the box stays yours
            to type the next thing into (the platform's rule for every chat box). */}
        <input
          style={skin ? { ...inputStyle, border: `1px solid ${line(0.28)}` } : inputStyle}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={skin ? skin.placeholder : 'Looking for something…'}
          autoFocus
        />
        {/* TALK OUT LOUD — the same call as anywhere in the World, started in
            this job. Press to call, press again to hang up (a toggle, never a
            hold). Never disables the box beside it. */}
        {canTalk && (
          <button
            type="button"
            onClick={toggleCall}
            aria-label={onCall ? 'Hang up' : 'Talk out loud'}
            title={onCall ? 'Hang up' : 'Talk out loud'}
            style={{
              cursor: 'pointer', borderRadius: 10, padding: '0 12px', font: '700 15px system-ui',
              border: `1px solid ${line(onCall ? 0.7 : 0.32)}`,
              background: onCall ? accent : line(0.1),
              color: onCall ? (skin ? skin.ink : '#08120a') : accent,
            }}
          >{onCall ? '■' : '🎙'}</button>
        )}
        <button
          type="submit"
          disabled={busy || !input.trim()}
          style={{
            cursor: busy || !input.trim() ? 'default' : 'pointer', border: 'none', borderRadius: 10,
            padding: '0 14px', font: '700 14px system-ui', color: skin ? skin.ink : '#08120a',
            background: busy || !input.trim() ? line(0.28) : accent,
          }}
        >{skin ? skin.send : 'Ask'}</button>
      </form>
    </div>
  );
}
